/** @jsx jsx */
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { Icon } from '@zapier/zinnia-icons';
import {
  Colors,
  Margin,
  Shadows,
  SkeletonText,
  Spacer,
  Text,
} from '@zapier/design-system';
import { css, jsx } from '@emotion/core';

import Divider from 'app/developer-v3/components/Divider';
import Link from 'app/common/components/Link';
import ManagerLayout from 'app/developer-v3/layouts/ManagerLayout';
import OAuthErrorMessage from 'app/developer-v3/components/OAuthErrorMessage';
import SaveButtonGroup from 'app/developer-v3/components/SaveButtonGroup';
import InputEditor, {
  InputValue,
} from 'app/developer-v3/components/InputEditor';
import { PartnerUserInteractionEventTracker } from 'app/developer-v3/teams/team-partner-sharing/createInteractionTracker';
import { ZAPIER_APP_BASE_URL } from 'app/common/constants';
import { getCSRFToken } from 'app/developer-v3/hooks/useCSRFToken';
import { useNotify, useOAuthApp } from 'app/developer-v3/hooks';
import { PARTNER_SOLUTIONS_BASE_DOC_URL } from 'app/developer-v3/constants';

const styleContainer = css`
  margin-top: 60px;
`;

const styleCard = css`
  display: flex;
  flex-direction: column;
  padding: 25px 40px;
  border: 1px solid ${Colors.GrayWarm3};
  border-radius: 8px;
  box-shadow: ${Shadows.elevation5};

  & b {
    display: block;
    margin-top: 5px;
    font-weight: 700;
  }
`;

const styleLink = css`
  display: flex;
  align-self: flex-start;
  gap: 5px;
  align-items: center;
  font-weight: 700;
`;

type Props = {
  appId: ID;
  slug: string;
  trackInteraction: PartnerUserInteractionEventTracker;
};

function Redirects({ slug, trackInteraction }: Props) {
  const oauthAppResult = useOAuthApp({ slug });
  const notify = useNotify();

  const [redirectURIs, setRedirectURIs] = useState<InputValue[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  async function saveRedirectURIs() {
    if (oauthAppResult.type !== 'ok') {
      throw new Error('Failed to save redirect URIs');
    }

    const eventId = uuidv4();
    const correlationId = uuidv4();

    // This event was caused by itself, so the causationId is the same as eventId
    const causationId = eventId;

    trackInteraction({
      eventAction: 'click',
      interactionName: 'save_redirect_uris',
      interactionGoal: 'Update redirect URIs for OAuth',
      eventId,
      correlationId,
      causationId,
    });

    setIsSaving(true);
    const nonEmptyRedirectURIs = redirectURIs
      .map(({ value }) => value)
      .filter(value => value);
    const url = new URL(
      `/api/v4/oauth/applications/${oauthAppResult.data.id}/`,
      ZAPIER_APP_BASE_URL
    );
    const response = await fetch(url.href, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': await getCSRFToken(),
      },
      method: 'PATCH',
      body: JSON.stringify({
        correlation_id: correlationId,
        causation_id: eventId,
        redirect_uris: nonEmptyRedirectURIs,
      }),
    });
    const json = await response.json();

    if (json.errors) {
      console.error(json.errors);
      notify.failure('Unable to update redirect URIs');
      if (json.errors[0].detail.redirect_uris) {
        setRedirectURIs(
          nonEmptyRedirectURIs.map((redirectURI, i) => {
            if (json.errors[0].detail.redirect_uris[i]) {
              return { value: redirectURI, isInvalidValue: true };
            }
            return { value: redirectURI, isInvalidValue: false };
          })
        );
      }
    } else {
      setRedirectURIs(json.redirect_uris.map(value => ({ value })));
      notify.success('Redirect URIs updated!');
    }

    setIsSaving(false);
  }

  useEffect(() => {
    if (oauthAppResult.type === 'ok') {
      setRedirectURIs(
        oauthAppResult.data.redirectUris.map(value => ({ value }))
      );
    }
  }, [oauthAppResult]);

  if (oauthAppResult.type === 'loading') {
    return (
      <Margin margin="30px 45px">
        <SkeletonText lines={5} size="heading" />
      </Margin>
    );
  }

  if (oauthAppResult.type === 'fail') {
    return <OAuthErrorMessage margin="30px 45px" />;
  }

  return (
    <ManagerLayout title="Redirect URIs (OAuth 2.0)">
      <Text margin="-10px 0 20px" tag="p">
        If you want to use the Get Zaps endpoint in the Workflow API, you need
        to authenticate with an access token using the OAuth 2.0 protocol. Enter
        redirect URIs so we can trust your site to receive the access token.
        These are callback URIs in your web app that your users will arrive at
        after authorizing with us in an OAuth flow.{' '}
        <Link
          href={`${PARTNER_SOLUTIONS_BASE_DOC_URL}/api-reference/authentication#retrieving-a-user-access-token`}
          style={{ textDecoration: 'underline' }}
          target="_blank"
        >
          Learn more about authentication.
        </Link>
      </Text>
      <InputEditor
        inputName="Enter redirect URI"
        items={redirectURIs}
        onUpdate={setRedirectURIs}
      />
      <Spacer height="20px" />
      <Divider />
      <SaveButtonGroup isSaving={isSaving} onSave={saveRedirectURIs} />
      <div css={styleContainer}>
        <div css={styleCard}>
          <Text tag="h2" type="Body3">
            Workflow API - Get Zaps
          </Text>
          <Text margin="10px 0" tag="p">
            Lets your users view their Zaps they've created with your
            integration inside your own product.
            <br />
            <b>Authentication: A user&apos;s access token</b>
          </Text>
          <Link
            css={styleLink}
            href={`${PARTNER_SOLUTIONS_BASE_DOC_URL}/api-reference/zaps/get-zaps-[v1]`}
            iconPosition="end"
          >
            Get started
            <Icon name="arrowSmallRight" size={19} />
          </Link>
        </div>
      </div>
    </ManagerLayout>
  );
}

export default Redirects;
